
<template>
    <div class="token-chart-container">
        <div class="chart-controls d-flex justify-content-between align-items-center mb-2">
            <div class="chart-type-toggle">
                <button :class="['chart-type-btn', chartType === 'candle' ? 'active' : '']"
                        @click="switchChartType('candle')" title="Candlestick">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <rect x="3" y="8" width="4" height="8" rx="1"/>
                        <rect x="10" y="4" width="4" height="16" rx="1"/>
                        <rect x="17" y="6" width="4" height="12" rx="1"/>
                    </svg>
                </button>
                <button :class="['chart-type-btn', chartType === 'line' ? 'active' : '']"
                        @click="switchChartType('line')" title="Line chart">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M3 12l4-4 4 6 4-8 6 6"/>
                    </svg>
                </button>
            </div>
            <div class="btn-group btn-group-sm" role="group" aria-label="Chart Intervals">
                <button type="button" v-for="interval in intervals" :key="interval.value"
                        :class="['btn', currentInterval === interval.value ? 'btn-primary' : 'btn-outline-secondary']"
                        @click="changeInterval(interval.value)">
                    {{ interval.label }}
                </button>
            </div>
        </div>
        <div class="chart-wrapper" ref="chartWrapper">
            <!-- OHLC Info Bar (Jupiter-style) -->
            <div class="chart-info-bar" v-if="allRawData.length > 0">
                <template v-if="chartType === 'candle'">
                    <span class="info-label">O</span><span class="info-value">{{ infoBar.open }}</span>
                    <span class="info-label">H</span><span class="info-value">{{ infoBar.high }}</span>
                    <span class="info-label">L</span><span class="info-value">{{ infoBar.low }}</span>
                    <span class="info-label">C</span><span class="info-value">{{ infoBar.close }}</span>
                    <span :class="['info-change', infoBar.changeNum >= 0 ? 'positive' : 'negative']">{{ infoBar.change }}</span>
                    <span class="info-sep"></span>
                    <span class="info-label">Vol</span><span class="info-value">{{ infoBar.volume }}</span>
                </template>
                <template v-else>
                    <span class="info-label">Price</span><span class="info-value">{{ infoBar.close }}</span>
                    <span :class="['info-change', infoBar.changeNum >= 0 ? 'positive' : 'negative']">{{ infoBar.change }}</span>
                </template>
            </div>
            <div ref="chartContainer" class="chart-area"></div>
            <!-- Current price label (HTML overlay on right axis) -->
            <div v-if="priceLabel.visible" class="price-label" :class="priceLabel.direction"
                 :style="{ top: priceLabel.y + 'px' }">
                {{ priceLabel.text }}
            </div>
        </div>
        <div v-if="isLoading && allRawData.length === 0 && !error" class="loading-overlay">Loading Chart...</div>
        <div v-if="error" class="error-overlay">{{ error }}</div>
    </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, nextTick } from 'vue';
import { createChart, CrosshairMode, LineStyle } from 'lightweight-charts';
import axios from 'axios';

const props = defineProps({
    mint: { type: String, required: true },
});

// ── Refs & State ────────────────────────────────────────────────
const chartContainer = ref(null);
const chartWrapper = ref(null);
const chart = ref(null);
const candleSeries = ref(null);
const volumeSeries = ref(null);
const areaSeries = ref(null);
const chartType = ref('candle'); // 'candle' | 'line'
const isLoading = ref(false);
const error = ref(null);
const isDarkMode = ref(false);
const wsConnected = ref(false);
const allRawData = ref([]); // Full OHLCV data: [{time, open, high, low, close, volume}, ...]
const earliestLoaded = ref(null);
const isFetchingMore = ref(false);
const noMoreData = ref(false);
let currentPriceLine = null;
let resizeObserver = null;
let themeObserver = null;
let fetchTimeout = null;
let reconciliationTimer = null;
let echoChannel = null;
// Track the current (in-progress) candle for proper OHLCV building from live trades
let currentCandle = null; // { time, open, high, low, close, volume }

// The "primary" series for price line & info bar
const primarySeries = computed(() =>
    chartType.value === 'candle' ? candleSeries.value : areaSeries.value
);

// ── Current Price Label (HTML overlay) ──────────────────────────
const priceLabel = reactive({ visible: false, y: 0, text: '', direction: 'up' });

const updatePriceLabel = () => {
    const series = primarySeries.value;
    if (!series || allRawData.value.length === 0) { priceLabel.visible = false; return; }

    const last = allRawData.value[allRawData.value.length - 1];
    const coord = series.priceToCoordinate(last.close);
    if (coord === null) { priceLabel.visible = false; return; }

    priceLabel.visible = true;
    priceLabel.y = coord - 10; // Center the label on the line
    priceLabel.text = formatInfoPrice(last.close);
    priceLabel.direction = last.close >= last.open ? 'up' : 'down';
};

// ── OHLC Info Bar (Jupiter-style) ───────────────────────────────
const infoBar = reactive({
    open: '', high: '', low: '', close: '',
    change: '', changeNum: 0, volume: '',
});

const setInfoBarFromCandle = (d) => {
    if (!d) return;
    infoBar.open   = formatInfoPrice(d.open);
    infoBar.high   = formatInfoPrice(d.high);
    infoBar.low    = formatInfoPrice(d.low);
    infoBar.close  = formatInfoPrice(d.close);
    infoBar.volume = formatVolume(d.volume);
    const chg = d.close - d.open;
    const pct = d.open !== 0 ? (chg / d.open * 100) : 0;
    infoBar.changeNum = chg;
    const sign = chg >= 0 ? '+' : '';
    infoBar.change = `${sign}${formatInfoPrice(chg)} (${sign}${pct.toFixed(2)}%)`;
};

const resetInfoBarToLatest = () => {
    if (allRawData.value.length === 0) return;
    setInfoBarFromCandle(allRawData.value[allRawData.value.length - 1]);
};

// ── Intervals with lazy-load config ─────────────────────────────
const intervals = [
    { label: '1m',  value: '1m',  range: 'now-9h',   chunkMs:  6 * 3600_000,  maxMs:   2 * 86_400_000 },
    { label: '5m',  value: '5m',  range: 'now-18h',  chunkMs: 12 * 3600_000,  maxMs:   7 * 86_400_000 },
    { label: '15m', value: '15m', range: 'now-36h',  chunkMs:      86_400_000, maxMs:  14 * 86_400_000 },
    { label: '1h',  value: '1h',  range: 'now-7d/h',  chunkMs:  3 * 86_400_000, maxMs:  90 * 86_400_000 },
    { label: '4h',  value: '4h',  range: 'now-11d',  chunkMs:  7 * 86_400_000, maxMs: 180 * 86_400_000 },
    { label: '1d',  value: '1d',  range: 'now-45d',  chunkMs: 30 * 86_400_000, maxMs: 365 * 86_400_000 },
    { label: '1w',  value: '1w',  range: 'now-120d', chunkMs: 90 * 86_400_000, maxMs: 730 * 86_400_000 },
];
const defaultInterval = intervals.find(i => i.value === '1h') || intervals[3];
const currentInterval = ref(defaultInterval.value);
const currentRange = ref(defaultInterval.range);

// ── Map interval to seconds (for reconciliation & live-trade candle bucketing) ──
const intervalToSeconds = {
    '1m': 60, '5m': 300, '15m': 900, '1h': 3600,
    '4h': 14400, '1d': 86400, '1w': 604800,
};

// ── Theme Configs ───────────────────────────────────────────────
const candleColors = {
    upColor: '#26a69a',
    downColor: '#ef5350',
    borderVisible: false,
    wickUpColor: '#26a69a',
    wickDownColor: '#ef5350',
};

const themes = {
    light: {
        layout: { background: { color: '#ffffff' }, textColor: '#333333' },
        grid: {
            vertLines: { color: '#f0f0f0' },
            horzLines: { color: '#f0f0f0' },
        },
        rightPriceScale: { borderColor: '#e0e0e0' },
        timeScale: { borderColor: '#e0e0e0' },
        area: {
            topColor: 'rgba(99, 102, 241, 0.28)',
            bottomColor: 'rgba(99, 102, 241, 0.02)',
            lineColor: '#6366f1',
            crosshairMarkerBackgroundColor: '#6366f1',
        },
        priceLine: 'rgba(99, 102, 241, 0.6)',
    },
    dark: {
        layout: { background: { color: '#1a1a2e' }, textColor: '#e6edf3' },
        grid: {
            vertLines: { color: 'rgba(139, 92, 246, 0.08)' },
            horzLines: { color: 'rgba(139, 92, 246, 0.08)' },
        },
        rightPriceScale: { borderColor: 'rgba(139, 92, 246, 0.2)' },
        timeScale: { borderColor: 'rgba(139, 92, 246, 0.2)' },
        area: {
            topColor: 'rgba(139, 92, 246, 0.35)',
            bottomColor: 'rgba(139, 92, 246, 0.02)',
            lineColor: '#8b5cf6',
            crosshairMarkerBackgroundColor: '#8b5cf6',
        },
        priceLine: 'rgba(139, 92, 246, 0.6)',
    },
};

// ── Formatting Helpers ──────────────────────────────────────────
const checkDarkMode = () =>
    document.body.classList.contains('dark-mode') ||
    document.documentElement.classList.contains('dark-mode');

const formatAxisPrice = (price) => {
    if (!price || price === 0) return '$0';
    const abs = Math.abs(price);
    if (abs >= 1e6) return '$' + (price / 1e6).toFixed(2) + 'M';
    if (abs >= 1e3) return '$' + (price / 1e3).toFixed(2) + 'K';
    if (abs >= 1) return '$' + price.toFixed(2);
    if (abs >= 0.01) return '$' + price.toFixed(4);
    if (abs >= 0.0001) return '$' + price.toFixed(6);
    return '$' + price.toFixed(8);
};

const formatInfoPrice = (price) => {
    if (price == null || price === 0) return '$0';
    const abs = Math.abs(price);
    if (abs >= 1e3) return '$' + price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (abs >= 1) return '$' + price.toFixed(2);
    if (abs >= 0.01) return '$' + price.toFixed(4);
    if (abs >= 0.0001) return '$' + price.toFixed(6);
    return '$' + price.toFixed(8);
};

const formatVolume = (vol) => {
    if (vol == null) return '';
    if (vol >= 1e9) return '$' + (vol / 1e9).toFixed(2) + 'B';
    if (vol >= 1e6) return '$' + (vol / 1e6).toFixed(2) + 'M';
    if (vol >= 1e3) return '$' + (vol / 1e3).toFixed(1) + 'K';
    return '$' + vol.toFixed(0);
};

const makeTickFormatter = () => (time) => {
    const date = new Date(time * 1000);
    const iv = currentInterval.value;
    if (['1d', '1w'].includes(iv)) {
        return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    }
    // All sub-daily intervals show date + time (safe when scrolling across days)
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
        + ' ' + date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
};

// Compute price format dynamically after data loads
const computePriceFormat = (prices) => {
    if (!prices || prices.length === 0) return { type: 'price', precision: 2, minMove: 0.01 };
    const mid = prices[Math.floor(prices.length / 2)];
    if (mid >= 1000) return { type: 'price', precision: 2, minMove: 0.01 };
    if (mid >= 1)    return { type: 'price', precision: 2, minMove: 0.01 };
    if (mid >= 0.01) return { type: 'price', precision: 4, minMove: 0.0001 };
    if (mid >= 0.0001) return { type: 'price', precision: 6, minMove: 0.000001 };
    return { type: 'price', precision: 8, minMove: 0.00000001 };
};

// Default used at creation; updated after data loads
let currentPriceFormat = { type: 'price', precision: 2, minMove: 0.01 };

// ── Series Management ───────────────────────────────────────────
const removePriceLine = () => {
    if (currentPriceLine && primarySeries.value) {
        try { primarySeries.value.removePriceLine(currentPriceLine); } catch {}
    }
    currentPriceLine = null;
};

const removeAllSeries = () => {
    removePriceLine();
    if (candleSeries.value) { chart.value.removeSeries(candleSeries.value); candleSeries.value = null; }
    if (volumeSeries.value) { chart.value.removeSeries(volumeSeries.value); volumeSeries.value = null; }
    if (areaSeries.value)   { chart.value.removeSeries(areaSeries.value);   areaSeries.value = null; }
};

const createSeries = () => {
    const theme = isDarkMode.value ? themes.dark : themes.light;

    if (chartType.value === 'candle') {
        candleSeries.value = chart.value.addCandlestickSeries({
            ...candleColors,
            priceFormat: currentPriceFormat,
            lastValueVisible: false,
            priceLineVisible: false, // We use HTML overlay for the price label
        });
        volumeSeries.value = chart.value.addHistogramSeries({
            color: '#26a69a',
            priceFormat: { type: 'volume' },
            priceScaleId: 'volume_scale',
            lastValueVisible: false,
            priceLineVisible: false,
        });
        chart.value.priceScale('volume_scale').applyOptions({
            scaleMargins: { top: 0.75, bottom: 0 },
        });
    } else {
        areaSeries.value = chart.value.addAreaSeries({
            ...theme.area,
            lineWidth: 2,
            crosshairMarkerVisible: true,
            crosshairMarkerRadius: 5,
            crosshairMarkerBorderColor: theme.area.lineColor,
            crosshairMarkerBorderWidth: 2,
            priceFormat: currentPriceFormat,
            lastValueVisible: false,
            priceLineVisible: false,
        });
    }
};

const applyDataToSeries = () => {
    if (allRawData.value.length === 0) return;

    // Update price format precision based on actual price range
    const prices = allRawData.value.map(d => d.close).sort((a, b) => a - b);
    currentPriceFormat = computePriceFormat(prices);
    const series = primarySeries.value;
    if (series) series.applyOptions({ priceFormat: currentPriceFormat });

    if (chartType.value === 'candle') {
        const candleData = allRawData.value.map(d => ({
            time: d.time, open: d.open, high: d.high, low: d.low, close: d.close,
        }));
        const volumeData = allRawData.value.map(d => ({
            time: d.time,
            value: d.volume,
            color: d.open <= d.close ? 'rgba(38, 166, 154, 0.5)' : 'rgba(239, 83, 80, 0.5)',
        }));
        candleSeries.value.setData(candleData);
        volumeSeries.value.setData(volumeData);
    } else {
        const lineData = allRawData.value.map(d => ({ time: d.time, value: d.close }));
        areaSeries.value.setData(lineData);
    }
};

// ── Chart Initialisation ────────────────────────────────────────
const initializeChart = () => {
    if (!chartContainer.value || chart.value) return;

    isDarkMode.value = checkDarkMode();
    const theme = isDarkMode.value ? themes.dark : themes.light;

    chart.value = createChart(chartContainer.value, {
        width: chartContainer.value.clientWidth,
        height: chartContainer.value.clientHeight,
        layout: theme.layout,
        grid: theme.grid,
        crosshair: {
            mode: CrosshairMode.Magnet,
            vertLine:  { visible: false, labelVisible: true },
            horzLine:  { visible: false, labelVisible: true },
        },
        rightPriceScale: {
            ...theme.rightPriceScale,
            autoScale: true,
        },
        timeScale: {
            ...theme.timeScale,
            timeVisible: true,
            secondsVisible: false,
            tickMarkFormatter: makeTickFormatter(),
        },
        handleScale: {
            mouseWheel: true,
            pinch: true,
            axisPressedMouseMove: { time: true, price: false },
        },
        handleScroll: {
            mouseWheel: false,
            pressedMouseMove: true,
            horzTouchDrag: true,
            vertTouchDrag: false,
        },
        localization: {
            timeFormatter: (time) => new Date(time * 1000).toLocaleString(),
        },
    });

    createSeries();

    // Info bar + crosshair labels on hover
    chart.value.subscribeCrosshairMove((param) => {
        handleCrosshairMove(param);
        updatePriceLabel(); // Keep label position synced with auto-scale
    });

    // Lazy-load older data when user scrolls/zooms near the left edge
    chart.value.timeScale().subscribeVisibleLogicalRangeChange((range) => {
        if (!range || isFetchingMore.value || noMoreData.value || !earliestLoaded.value) return;
        if (range.from <= 10) {
            // Compute how many bars of empty space are visible beyond loaded data
            const emptyBars = Math.max(0, -range.from) + 20; // plus buffer
            clearTimeout(fetchTimeout);
            fetchTimeout = setTimeout(() => fetchOlderData(emptyBars), 300);
        }
    });

    // Resize
    resizeObserver = new ResizeObserver(entries => {
        const { width, height } = entries[0].contentRect;
        chart.value?.applyOptions({ width, height });
    });
    resizeObserver.observe(chartContainer.value);

    fetchChartData();
};

// ── Data Fetching ───────────────────────────────────────────────
// parseResponse: used for the main OHLCV fetch (timestamps in ms from API)
const parseResponse = (responseData) =>
    responseData
        .map(d => ({
            time: d.timestamp / 1000,
            open: d.open,
            high: d.high,
            low: d.low,
            close: d.close,
            volume: d.volume,
        }))
        .filter(d => d.close != null && d.close > 0);

// Gap-fill helpers (convert raw API format to lightweight-charts format)
const toCandle = (d) => ({
    time: d.timestamp / 1000,
    open: d.open,
    high: d.high,
    low: d.low,
    close: d.close,
});

const toVolume = (d) => ({
    time: d.timestamp / 1000,
    value: d.volume,
    color: d.open <= d.close ? 'rgba(38, 166, 154, 0.5)' : 'rgba(239, 83, 80, 0.5)',
});

/**
 * Fetch gap-fill candles from raw trades since a given timestamp.
 * Returns { candles, volumes, raw } in lightweight-charts format.
 */
const fetchGapFill = async (sinceMs) => {
    try {
        const response = await axios.get(`/api/tokens/${props.mint}/candles`, {
            params: {
                resolution: currentInterval.value,
                since: sinceMs,
            }
        });
        const data = response.data || [];
        return {
            candles: data.map(toCandle).filter(d => d.open > 0),
            volumes: data.map(toVolume).filter(d => d.value != null),
            raw: data.filter(d => d.open > 0).map(d => ({
                time: d.timestamp / 1000,
                open: d.open,
                high: d.high,
                low: d.low,
                close: d.close,
                volume: d.volume,
            })),
        };
    } catch (err) {
        console.error('Error fetching gap-fill candles:', err);
        return { candles: [], volumes: [], raw: [] };
    }
};

/**
 * Merge gap-fill candles into allRawData and update the chart series.
 * Uses update() for incremental additions to avoid full redraw.
 */
const mergeGapFill = (gapFill) => {
    const { candles, volumes, raw } = gapFill;
    if (raw.length === 0) return;

    // Merge into allRawData (single source of truth)
    const existingTimes = new Set(allRawData.value.map(d => d.time));
    for (const point of raw) {
        if (existingTimes.has(point.time)) {
            // Update existing candle in allRawData
            const idx = allRawData.value.findIndex(d => d.time === point.time);
            if (idx !== -1) allRawData.value[idx] = point;
        } else {
            allRawData.value.push(point);
        }
    }
    allRawData.value.sort((a, b) => a.time - b.time);

    // Incrementally update chart series
    if (chartType.value === 'candle') {
        if (candleSeries.value && volumeSeries.value) {
            for (const candle of candles) {
                candleSeries.value.update(candle);
            }
            for (const vol of volumes) {
                volumeSeries.value.update(vol);
            }
        }
    } else {
        if (areaSeries.value) {
            for (const candle of candles) {
                areaSeries.value.update({ time: candle.time, value: candle.close });
            }
        }
    }

    // Update UI state
    updatePriceLine();
    resetInfoBarToLatest();
    requestAnimationFrame(() => updatePriceLabel());
};

const fetchChartData = async () => {
    isLoading.value = true;
    error.value = null;
    // Don't clear allRawData — keep old chart visible while loading new data
    noMoreData.value = false;

    // Clear any existing reconciliation timer and candle state
    if (reconciliationTimer) {
        clearInterval(reconciliationTimer);
        reconciliationTimer = null;
    }
    currentCandle = null;

    try {
        const response = await axios.get(`/api/tokens/${props.mint}/ohlcv`, {
            params: { interval: currentInterval.value, from: currentRange.value, to: 'now' },
        });

        const data = parseResponse(response.data);
        if (data.length === 0) {
            allRawData.value = [];
            error.value = 'No chart data available.';
            return;
        }

        allRawData.value = data;
        earliestLoaded.value = data[0].time * 1000;

        applyDataToSeries();
        chart.value.timeScale().fitContent();
        updatePriceLine();
        resetInfoBarToLatest();
        // Wait one frame for chart to render before positioning label
        requestAnimationFrame(() => updatePriceLabel());

        // Fetch gap-fill candles since last transform candle
        const lastCandleTimeMs = data[data.length - 1].time * 1000;
        const gapFill = await fetchGapFill(lastCandleTimeMs);
        mergeGapFill(gapFill);

        // Set up periodic reconciliation
        startReconciliation();
    } catch (err) {
        console.error('Chart data error:', err);
        error.value = 'Failed to load chart data.';
    } finally {
        isLoading.value = false;
    }
};

// Map interval to bar duration in ms for computing fetch windows
const barDurationMs = {
    '1m': 60_000, '5m': 300_000, '15m': 900_000,
    '1h': 3_600_000, '4h': 14_400_000, '1d': 86_400_000, '1w': 604_800_000,
};

const fetchOlderData = async (barsNeeded = 0) => {
    if (isFetchingMore.value || noMoreData.value || !earliestLoaded.value) return;

    const interval = intervals.find(i => i.value === currentInterval.value);
    if (!interval) return;

    const maxFrom = Date.now() - interval.maxMs;
    if (earliestLoaded.value <= maxFrom) { noMoreData.value = true; return; }

    isFetchingMore.value = true;
    try {
        const toMs  = earliestLoaded.value;
        // Fetch enough to cover the visible empty space, at least one chunk
        const neededMs = barsNeeded > 0
            ? barsNeeded * (barDurationMs[currentInterval.value] || interval.chunkMs)
            : interval.chunkMs;
        const fetchMs = Math.max(interval.chunkMs, neededMs);
        const fromMs = Math.max(toMs - fetchMs, maxFrom);
        if (fromMs >= toMs) { noMoreData.value = true; return; }

        const response = await axios.get(`/api/tokens/${props.mint}/ohlcv`, {
            params: {
                interval: currentInterval.value,
                from: new Date(fromMs).toISOString(),
                to:   new Date(toMs).toISOString(),
            },
        });

        if (response.data.length === 0) { noMoreData.value = true; return; }

        const newData = parseResponse(response.data);
        const existingTimes = new Set(allRawData.value.map(d => d.time));
        const unique = newData.filter(d => !existingTimes.has(d.time));

        if (unique.length > 0) {
            allRawData.value = [...unique, ...allRawData.value].sort((a, b) => a.time - b.time);
            applyDataToSeries();
            // Don't call setVisibleRange — setData() preserves the current scroll position
            // and setVisibleRange was causing the snap-back bug
            earliestLoaded.value = allRawData.value[0].time * 1000;
        } else {
            noMoreData.value = true;
        }
    } catch (err) {
        console.error('Fetch older data error:', err);
    } finally {
        isFetchingMore.value = false;
    }
};

// ── Current-Price Line ──────────────────────────────────────────
const updatePriceLine = () => {
    const series = primarySeries.value;
    if (!series || allRawData.value.length === 0) return;

    const last = allRawData.value[allRawData.value.length - 1];
    const isUp = last.close >= last.open;
    const lineColor = isUp ? '#26a69a' : '#ef5350';

    if (currentPriceLine) {
        currentPriceLine.applyOptions({ price: last.close, color: lineColor });
    } else {
        currentPriceLine = series.createPriceLine({
            price: last.close,
            color: lineColor,
            lineWidth: 1,
            lineStyle: LineStyle.Dashed,
            axisLabelVisible: false, // Series lastValueVisible handles the colored axis label
            title: '',
        });
    }
};

// ── Crosshair → Info Bar ────────────────────────────────────────
const handleCrosshairMove = (param) => {
    if (!param.time || !param.seriesPrices) {
        resetInfoBarToLatest();
        return;
    }

    // Look up the raw candle for this timestamp
    const candle = allRawData.value.find(d => d.time === param.time);
    if (candle) {
        setInfoBarFromCandle(candle);
    } else {
        // Fallback: read from series data
        const series = primarySeries.value;
        if (!series) return;
        const data = param.seriesPrices.get(series);
        if (data === undefined) return;

        if (chartType.value === 'candle' && typeof data === 'object') {
            const vol = volumeSeries.value ? param.seriesPrices.get(volumeSeries.value) : null;
            setInfoBarFromCandle({ open: data.open, high: data.high, low: data.low, close: data.close, volume: vol });
        } else {
            const price = typeof data === 'object' ? data.close : data;
            infoBar.close = formatInfoPrice(price);
            infoBar.open = infoBar.high = infoBar.low = '';
            infoBar.volume = '';
            infoBar.change = '';
            infoBar.changeNum = 0;
        }
    }
};

// ── Reconciliation (periodic gap-fill to catch late-arriving data) ──
const startReconciliation = () => {
    if (reconciliationTimer) {
        clearInterval(reconciliationTimer);
    }

    const seconds = intervalToSeconds[currentInterval.value] || 60;
    // Reconcile at least every 60s, cap at 5 minutes so stale tabs catch up quickly
    const intervalMs = Math.min(Math.max(seconds, 60), 300) * 1000;

    reconciliationTimer = setInterval(async () => {
        // Re-fetch gap-fill from 2 intervals ago to catch any late-arriving data
        const sinceMs = Date.now() - (seconds * 2 * 1000);
        const gapFill = await fetchGapFill(sinceMs);
        mergeGapFill(gapFill);
    }, intervalMs);
};

// ── WebSocket Live Trade Updates ────────────────────────────────
/**
 * Handle a live trade from WebSocket.
 * Updates the current candle or creates a new one at period boundaries.
 * Updates allRawData as single source of truth, chart series, and info bar.
 */
const handleLiveTrade = (trade) => {
    // Determine price: use the non-SOL token's price
    let priceUsd = 0;
    let volume = 0;

    if (trade.token_a && trade.token_b) {
        if (trade.token_a.id === props.mint) {
            priceUsd = trade.token_a.price_usd / 1e12;
            volume = trade.token_a.amount / Math.pow(10, trade.token_a.decimals || 0);
        } else if (trade.token_b.id === props.mint) {
            priceUsd = trade.token_b.price_usd / 1e12;
            volume = trade.token_b.amount / Math.pow(10, trade.token_b.decimals || 0);
        } else {
            return; // Trade doesn't involve our token
        }
    }

    if (priceUsd <= 0) return;

    const tradeTime = new Date(trade.timestamp).getTime() / 1000;
    const periodSeconds = intervalToSeconds[currentInterval.value] || 60;
    const candleTime = Math.floor(tradeTime / periodSeconds) * periodSeconds;

    // Build proper OHLCV: track current candle state in memory
    if (!currentCandle || currentCandle.time !== candleTime) {
        // New candle period -- start fresh
        currentCandle = {
            time: candleTime,
            open: priceUsd,
            high: priceUsd,
            low: priceUsd,
            close: priceUsd,
            volume: volume,
        };
    } else {
        // Same period -- update high/low/close and accumulate volume
        currentCandle.high = Math.max(currentCandle.high, priceUsd);
        currentCandle.low = Math.min(currentCandle.low, priceUsd);
        currentCandle.close = priceUsd;
        currentCandle.volume += volume;
    }

    // Update allRawData (single source of truth)
    const existingIdx = allRawData.value.findIndex(d => d.time === candleTime);
    const updatedCandle = { ...currentCandle };
    if (existingIdx !== -1) {
        allRawData.value[existingIdx] = updatedCandle;
    } else {
        allRawData.value.push(updatedCandle);
        allRawData.value.sort((a, b) => a.time - b.time);
    }

    // Update the appropriate chart series
    if (chartType.value === 'candle') {
        if (candleSeries.value) {
            candleSeries.value.update({
                time: currentCandle.time,
                open: currentCandle.open,
                high: currentCandle.high,
                low: currentCandle.low,
                close: currentCandle.close,
            });
        }
        if (volumeSeries.value) {
            volumeSeries.value.update({
                time: currentCandle.time,
                value: currentCandle.volume,
                color: currentCandle.close >= currentCandle.open
                    ? 'rgba(38, 166, 154, 0.5)'
                    : 'rgba(239, 83, 80, 0.5)',
            });
        }
    } else {
        if (areaSeries.value) {
            areaSeries.value.update({
                time: currentCandle.time,
                value: currentCandle.close,
            });
        }
    }

    // Update info bar and price label with latest data
    updatePriceLine();
    resetInfoBarToLatest();
    requestAnimationFrame(() => updatePriceLabel());
};

/**
 * Subscribe to WebSocket channel for live trade updates.
 */
const subscribeToLiveUpdates = () => {
    if (!window.Echo) return;

    // Unsubscribe from previous channel
    unsubscribeFromLiveUpdates();

    echoChannel = window.Echo.channel(`token.${props.mint}`);
    echoChannel.listen('.trade.executed', (trade) => {
        wsConnected.value = true;
        handleLiveTrade(trade);
    });
};

const unsubscribeFromLiveUpdates = () => {
    if (echoChannel) {
        window.Echo.leave(`token.${props.mint}`);
        echoChannel = null;
    }
};

// ── Chart Type Toggle ───────────────────────────────────────────
const switchChartType = (type) => {
    if (type === chartType.value) return;

    // Preserve current visible range across the switch
    const visibleRange = chart.value.timeScale().getVisibleLogicalRange();

    chartType.value = type;
    removeAllSeries();
    createSeries();
    applyDataToSeries();
    updatePriceLine();
    resetInfoBarToLatest();

    if (visibleRange) {
        chart.value.timeScale().setVisibleLogicalRange(visibleRange);
    } else {
        chart.value.timeScale().fitContent();
    }
};

// ── Theme Switching ─────────────────────────────────────────────
const applyTheme = () => {
    if (!chart.value) return;
    isDarkMode.value = checkDarkMode();
    const theme = isDarkMode.value ? themes.dark : themes.light;

    chart.value.applyOptions({
        layout: theme.layout,
        grid: theme.grid,
        rightPriceScale: theme.rightPriceScale,
        timeScale: { ...theme.timeScale, timeVisible: true, secondsVisible: false },
    });

    if (areaSeries.value) areaSeries.value.applyOptions(theme.area);
    updatePriceLine();
};

// ── Interval Change ─────────────────────────────────────────────
const changeInterval = (interval) => {
    const selected = intervals.find(i => i.value === interval);
    if (!selected) return;
    currentInterval.value = selected.value;
    currentRange.value    = selected.range;
    noMoreData.value      = false;
    earliestLoaded.value  = null; // Prevent stale lazy-loads during fetch

    if (chart.value) {
        chart.value.timeScale().applyOptions({ tickMarkFormatter: makeTickFormatter() });
    }
    removePriceLine();
    fetchChartData();
};

// ── Lifecycle ───────────────────────────────────────────────────
onMounted(async () => {
    await nextTick();
    initializeChart();

    themeObserver = new MutationObserver((mutations) => {
        for (const m of mutations) { if (m.attributeName === 'class') { applyTheme(); break; } }
    });
    themeObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

    // Subscribe to live WebSocket updates
    subscribeToLiveUpdates();
});

onUnmounted(() => {
    // Clean up WebSocket subscription
    unsubscribeFromLiveUpdates();

    // Clean up reconciliation timer
    if (reconciliationTimer) {
        clearInterval(reconciliationTimer);
        reconciliationTimer = null;
    }

    if (resizeObserver && chartContainer.value) resizeObserver.unobserve(chartContainer.value);
    if (themeObserver) themeObserver.disconnect();
    clearTimeout(fetchTimeout);
    if (chart.value) { chart.value.remove(); chart.value = null; }
});
</script>

<style scoped>
.token-chart-container {
    position: relative;
    display: flex;
    flex-direction: column;
    height: 420px;
    border-radius: 8px;
    overflow: hidden;
}
.chart-controls { flex-shrink: 0; }
.chart-wrapper {
    position: relative;
    flex: 1;
    min-height: 0;
}
.chart-area {
    width: 100%;
    height: 100%;
}

/* ── Chart Type Toggle ───────────────────────────────────────── */
.chart-type-toggle {
    display: flex;
    gap: 2px;
    background: rgba(0, 0, 0, 0.05);
    border-radius: 6px;
    padding: 2px;
}
.chart-type-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 28px;
    border: none;
    border-radius: 4px;
    background: transparent;
    color: #888;
    cursor: pointer;
    transition: all 0.15s ease;
}
.chart-type-btn:hover { color: #555; background: rgba(0, 0, 0, 0.05); }
.chart-type-btn.active { color: #6366f1; background: #fff; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }

:global(.dark-mode) .chart-type-toggle { background: rgba(255, 255, 255, 0.06); }
:global(.dark-mode) .chart-type-btn { color: #888; }
:global(.dark-mode) .chart-type-btn:hover { color: #bbb; background: rgba(255, 255, 255, 0.06); }
:global(.dark-mode) .chart-type-btn.active { color: #8b5cf6; background: rgba(139, 92, 246, 0.15); box-shadow: none; }

/* ── OHLC Info Bar ───────────────────────────────────────────── */
.chart-info-bar {
    position: absolute;
    top: 6px;
    left: 8px;
    z-index: 5;
    pointer-events: none;
    font-size: 11px;
    line-height: 1.6;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    color: #555;
}
.info-label {
    font-weight: 600;
    color: #999;
    margin-right: 2px;
}
.info-value {
    margin-right: 8px;
    color: #333;
}
.info-sep {
    display: inline-block;
    width: 1px;
    height: 10px;
    background: rgba(0,0,0,0.12);
    vertical-align: middle;
    margin: 0 6px;
}
.info-change {
    font-weight: 600;
    margin-right: 6px;
}
.info-change.positive { color: #26a69a; }
.info-change.negative { color: #ef5350; }

:global(.dark-mode) .chart-info-bar { color: #999; }
:global(.dark-mode) .info-label { color: #666; }
:global(.dark-mode) .info-value { color: #ccc; }
:global(.dark-mode) .info-sep { background: rgba(255,255,255,0.1); }

/* ── Current Price Label ─────────────────────────────────────── */
.price-label {
    position: absolute;
    right: 0;
    z-index: 4;
    padding: 2px 6px;
    font-size: 11px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: #fff;
    border-radius: 3px 0 0 3px;
    pointer-events: none;
    white-space: nowrap;
    line-height: 1.4;
}
.price-label.up   { background: #26a69a; }
.price-label.down { background: #ef5350; }

/* ── Overlays ─────────────────────────────────────────────────── */
.loading-overlay, .error-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    background: rgba(255, 255, 255, 0.9);
    font-size: 1.1em;
    color: #555;
    backdrop-filter: blur(4px);
}
.error-overlay { color: #ef4444; }

:global(.dark-mode) .loading-overlay,
:global(.dark-mode) .error-overlay {
    background: rgba(26, 26, 46, 0.9);
    color: #e6edf3;
}
:global(.dark-mode) .error-overlay { color: #ef4444; }
</style>
